import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { connLink } from 'src/global';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { UserNotification } from 'node_js/models/tables/user_notification';
import { UserAuthService } from './user-auth.service';
import { UserContact } from 'node_js/models/tables/user_contact';
import { IoCodeEnum } from 'node_js/models/enums/io-code';
import { ComponentComplete } from 'node_js/models/business/lab/component-complete';
import { LabComplete } from 'node_js/models/business/lab/lab-complete';
import { ComponentSettingsReturn } from 'node_js/models/business/component-settings-return';
import { Component } from 'node_js/models/tables/component';
import { ComponentSchedule } from 'node_js/models/tables/component_schedule';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { GadgetUser } from 'node_js/models/tables/gadget_user';
import { GadgetUserComplete } from 'node_js/models/business/lab/gadget-user-complete';
import { LabUserComplete } from 'node_js/models/business/lab/lab-user-complete';
import { LabUserCombo } from 'node_js/models/business/lab/lab-user-combo';
import { GadgetUsersUpdate } from 'node_js/models/business/lab/gadget-users-update';
import { ioLabSettings } from 'node_js/models/business/lab/io-lab-settings';
import { LabControllerComplete } from 'node_js/models/business/lab/lab-controller-complete';
import { ioGadgetSettings } from 'node_js/models/business/lab/io-gadget-settings';
import { IoRoomHeaderEnum } from 'node_js/models/enums/io-room-header';
import { DevicePresence } from 'node_js/models/tables/device_presence';

@Injectable({
  providedIn: 'root'
})
export class LiveUpdatesService {

  socket: Socket;
  public afterUserContactUpdatedObservable: Subject<UserContact>;
  public reconnected$ = new Subject<void>();
  private isConnecting = false; // Prevent multiple connection attempts
  private hasConnectedOnce = false; // Distinguish initial connect from reconnects
  private reconnectUserAuthService: UserAuthService = null;
  private wasManuallyDisconnected = false;
  private devicePresenceUpdatedSubject = new Subject<DevicePresence>();
  private readonly devicePresenceUpdatedHandler = (devicePresence: DevicePresence) => {
    this.ngZone.run(() => {
      this.devicePresenceUpdatedSubject.next(devicePresence);
    });
  };
  private readonly socketConnectedHandler = () => {
    console.log('[LiveUpdates] Socket connected successfully, ID:', this.socket?.id);
    console.log('[LiveUpdates] Transport:', this.socket?.io?.engine?.transport?.name);
    this.isConnecting = false;
    this.wasManuallyDisconnected = false;

    // socket.on('reconnect') is a Manager event in Socket.IO v4 and doesn't reliably
    // fire on the Socket instance. Detect reconnects here instead via the connect event.
    if (this.hasConnectedOnce) {
      console.log('[LiveUpdates] Reconnect detected via connect event');
      this.ngZone.run(() => this.reconnected$.next());
    }
    this.hasConnectedOnce = true;
  };
  private readonly socketConnectErrorHandler = (error: Error) => {
    console.error('[LiveUpdates] Socket connection error:', error);
    console.error('[LiveUpdates] Error details:', {
      message: error?.message,
      name: error?.name
    });
    console.error('[LiveUpdates] Connection URL:', connLink);
    this.isConnecting = false;
  };
  private readonly socketDisconnectHandler = (reason: string) => {
    console.warn('[LiveUpdates] Socket disconnected:', reason);
    this.isConnecting = false;

    if (!this.wasManuallyDisconnected && reason === 'io server disconnect') {
      console.warn('[LiveUpdates] Server disconnected the socket. Attempting reconnect...');
      void this.reconnectExistingSocket(`disconnect:${reason}`);
    }
  };
  private readonly socketReconnectHandler = (attemptNumber: number) => {
    console.log('[LiveUpdates] Socket.IO Manager reconnect event (attempt', attemptNumber, ')');
  };
  private readonly testIOResponseHandler = (data: any) => {
    console.log('[LiveUpdates] RECEIVED testIOResponse:', data);
    this.matSnackBar.open('IO Update Received!', 'Close', { duration: 3000 });
  };


  constructor(private matSnackBar: MatSnackBar, private ngZone: NgZone) {
    this.afterUserContactUpdatedObservable = new Subject<UserContact>();

  }

  async connect(userAuthService: UserAuthService) {
    this.reconnectUserAuthService = userAuthService;
    this.wasManuallyDisconnected = false;

    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('[LiveUpdates] Connection already in progress, skipping...');
      return;
    }

    if (this.socket) {
      if (this.socket.connected) {
        console.log('[LiveUpdates] Socket already connected, skipping...');
        return;
      }

      console.log('[LiveUpdates] Reconnecting existing socket instance...');
      await this.reconnectExistingSocket('connect() reuse existing socket');
      return;
    }

    console.log('[LiveUpdates] connect() called, server URL:', connLink);
    this.isConnecting = true;

    try {
      const auth = await this.buildSocketAuth(userAuthService);

      console.log('[LiveUpdates] Connecting to socket...');

      // Mobile-specific configuration
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Note: io() is synchronous and returns the socket instance immediately in v4
      this.socket = io(connLink, {
        auth,
        transports: ['polling', 'websocket'], // Use polling first, then upgrade to WebSocket
        timeout: isMobile ? 30000 : 20000, // Longer timeout for mobile
        forceNew: true, // Force new connection
        reconnection: true, // Enable reconnection
        reconnectionAttempts: isMobile ? 10 : 5, // More attempts for mobile
        reconnectionDelay: 1000, // Start with 1 second delay
        reconnectionDelayMax: isMobile ? 10000 : 5000, // Max delay for mobile
        upgrade: true, // Allow transport upgrade
        rememberUpgrade: true, // Remember successful upgrade
        withCredentials: false, // Disable credentials for CORS
        extraHeaders: {
          'Access-Control-Allow-Origin': '*'
        }
      });

      this.bindDevicePresenceUpdates();
      this.attachSocketLifecycleHandlers();

      console.log('[LiveUpdates] Socket connection initiated');
    } catch (error) {
      console.error('[LiveUpdates] Error during connection setup:', error);
      this.isConnecting = false;
    }
  }

  isConnected(): boolean {
    return this.socket && this.socket.connected;
  }

  async checkServerAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${connLink}/testConnection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}),
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      console.warn('[LiveUpdates] Server availability check failed:', error);
      return false;
    }
  }

  async checkWebSocketAvailability(): Promise<boolean> {
    return new Promise((resolve) => {
      const testSocket = io(connLink, {
        transports: ['websocket'],
        timeout: 5000,
        forceNew: true
      });

      const timeout = setTimeout(() => {
        testSocket.disconnect();
        resolve(false);
      }, 5000);

      testSocket.on('connect', () => {
        clearTimeout(timeout);
        testSocket.disconnect();
        resolve(true);
      });

      testSocket.on('connect_error', () => {
        clearTimeout(timeout);
        testSocket.disconnect();
        resolve(false);
      });
    });
  }

  joinRoom(ioRoomHeaderEnum: IoRoomHeaderEnum, value: string) {
    const roomName = ioRoomHeaderEnum + value;
    console.log("[LiveUpdates] joinRoom called:", { ioRoomHeaderEnum, value, roomName });
    console.log("[LiveUpdates] Socket status before join:", {
      exists: !!this.socket,
      connected: this.socket?.connected,
      id: this.socket?.id
    });

    if (!this.socket || !this.socket.connected) {
      console.error("[LiveUpdates] Cannot join room - socket not connected");
      return;
    }

    this.socket.emit("joinRoom", roomName, (response?: { ok?: boolean; room?: string; rooms?: string[]; error?: string }) => {
      if (!response?.ok) {
        console.error("[LiveUpdates] joinRoom failed:", { roomName, response });
        return;
      }

      console.log("[LiveUpdates] Successfully joined room:", response);
    });

    console.log("[LiveUpdates] joinRoom emit sent for room:", roomName);
  }

  leaveRoom(room: string) {
    console.log("[LiveUpdates] leaveRoom called:", room);
    if (!this.socket || !this.socket.connected) {
      console.error("[LiveUpdates] Cannot leave room - socket not connected");
      return;
    }

    this.socket.emit("leaveRoom", room, (response?: { ok?: boolean; room?: string; rooms?: string[]; error?: string }) => {
      if (!response?.ok) {
        console.error("[LiveUpdates] leaveRoom failed:", { room, response });
        return;
      }

      console.log("[LiveUpdates] Successfully left room:", response);
    });

    console.log("[LiveUpdates] leaveRoom emit sent for room:", room);
  }

  // Join all lab rooms for labs the user is a member of
  joinAllUserLabRooms(userAuthService: any): void {
    console.log("[LiveUpdates] joinAllUserLabRooms called");
    console.log("[LiveUpdates] Socket status:", {
      exists: !!this.socket,
      connected: this.socket?.connected,
      id: this.socket?.id
    });

    if (!this.socket || !this.socket.connected) {
      console.error("[LiveUpdates] Cannot join rooms - socket not connected");
      return;
    }

    if (!userAuthService.currentUserComplete?.labsComplete) {
      console.log("[LiveUpdates] No labs to join - user has no labs");
      return;
    }

    const labCount = userAuthService.currentUserComplete.labsComplete.length;
    console.log(`[LiveUpdates] Joining ${labCount} lab rooms`);

    for (let i = 0; i < labCount; i++) {
      const labComplete = userAuthService.currentUserComplete.labsComplete[i];
      console.log(`[LiveUpdates] Joining room for lab: ${labComplete.lab.name} (ID: ${labComplete.lab.id})`);
      this.joinRoom(IoRoomHeaderEnum.LAB, labComplete.lab.id);
    }

    console.log("[LiveUpdates] Finished joining all lab rooms");
  }

  // Leave a specific lab room (only when user is kicked/removed)
  leaveLabRoom(labId: string): void {
    const roomName = IoRoomHeaderEnum.LAB + labId;
    console.log("[LiveUpdates] leaveLabRoom called for labId:", labId, "roomName:", roomName);
    this.leaveRoom(roomName);
  }

  disconnect() {
    if (this.socket) {
      console.log('[LiveUpdates] Disconnecting socket...');
      this.wasManuallyDisconnected = true;
      this.reconnectUserAuthService = null;
      this.socket.off(IoCodeEnum.DevicePresenceUpdated, this.devicePresenceUpdatedHandler);
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      this.hasConnectedOnce = false; // Reset so next connect after login isn't treated as reconnect
    }
  }

  private async buildSocketAuth(userAuthService: UserAuthService): Promise<{ idToken: string, uid: string }> {
    const rawIdToken = await userAuthService.createIdToken(true); // force-refresh — token may have expired during background
    const idToken = typeof rawIdToken === 'string' ? rawIdToken : String(rawIdToken ?? '');

    return {
      idToken,
      uid: userAuthService.currentUserComplete.user.authUid
    };
  }

  private attachSocketLifecycleHandlers() {
    if (!this.socket) {
      return;
    }

    this.socket.off('connect', this.socketConnectedHandler);
    this.socket.off('connect_error', this.socketConnectErrorHandler);
    this.socket.off('disconnect', this.socketDisconnectHandler);
    this.socket.off('reconnect', this.socketReconnectHandler);
    this.socket.off('testIOResponse', this.testIOResponseHandler);

    this.socket.on('connect', this.socketConnectedHandler);
    this.socket.on('connect_error', this.socketConnectErrorHandler);
    this.socket.on('disconnect', this.socketDisconnectHandler);
    this.socket.on('reconnect', this.socketReconnectHandler);
    this.socket.on('testIOResponse', this.testIOResponseHandler);
  }

  private waitForSocketConnection(timeoutMs = 5000): Promise<boolean> {
    const socket = this.socket;

    if (!socket) {
      return Promise.resolve(false);
    }

    if (socket.connected) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
      const cleanup = () => {
        clearTimeout(timeoutId);
        socket.off('connect', onConnect);
        socket.off('connect_error', onConnectError);
      };

      const onConnect = () => {
        cleanup();
        resolve(true);
      };

      const onConnectError = () => {
        cleanup();
        resolve(false);
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(socket.connected);
      }, timeoutMs);

      socket.on('connect', onConnect);
      socket.on('connect_error', onConnectError);
    });
  }

  private async reconnectExistingSocket(context: string): Promise<boolean> {
    if (!this.socket) {
      return false;
    }

    if (this.socket.connected) {
      return true;
    }

    if (this.isConnecting) {
      return this.waitForSocketConnection();
    }

    if (!this.reconnectUserAuthService) {
      console.warn(`[LiveUpdates] Cannot reconnect socket (${context}) - no auth service available`);
      return false;
    }

    try {
      this.isConnecting = true;
      this.socket.auth = await this.buildSocketAuth(this.reconnectUserAuthService);
      this.attachSocketLifecycleHandlers();
      console.log(`[LiveUpdates] Attempting socket reconnect (${context})...`);
      this.socket.connect();
      return await this.waitForSocketConnection();
    } catch (error) {
      console.error(`[LiveUpdates] Failed to reconnect socket (${context}):`, error);
      this.isConnecting = false;
      return false;
    }
  }

  private async ensureSocketConnected(context: string): Promise<boolean> {
    if (this.socket?.connected) {
      return true;
    }

    if (this.socket) {
      return this.reconnectExistingSocket(context);
    }

    if (!this.reconnectUserAuthService) {
      return false;
    }

    await this.connect(this.reconnectUserAuthService);
    return this.waitForSocketConnection();
  }

  private bindDevicePresenceUpdates() {
    if (!this.socket) {
      return;
    }

    this.socket.off(IoCodeEnum.DevicePresenceUpdated, this.devicePresenceUpdatedHandler);
    this.socket.on(IoCodeEnum.DevicePresenceUpdated, this.devicePresenceUpdatedHandler);
  }

  private createSocketEventObservable<T>(eventName: string, missingSocketMessage: string, onEvent?: (data: T) => void): Observable<T> {
    return new Observable<T>((observer) => {
      const socket = this.socket;

      if (!socket) {
        console.error(missingSocketMessage);
        observer.complete();
        return undefined;
      }

      const handler = (data: T) => {
        onEvent?.(data);
        observer.next(data);
      };

      socket.on(eventName, handler);

      return () => {
        socket.off(eventName, handler);
      };
    });
  }

  onComponentValueUpdates(): Observable<Component> {
    return this.createSocketEventObservable<Component>(
      IoCodeEnum.ComponentValue,
      '[LiveUpdates] Cannot set up ComponentValue listener - socket not initialized'
    );
  }

  onDeletedLabUpdates(): Observable<string> {
    return this.createSocketEventObservable<string>(
      IoCodeEnum.DeletedLab,
      '[LiveUpdates] Cannot set up DeletedLab listener - socket not initialized'
    );
  }

  onDeletedGadgetUpdates(): Observable<string> {
    return this.createSocketEventObservable<string>(
      IoCodeEnum.DeletedGadget,
      '[LiveUpdates] Cannot set up DeletedGadget listener - socket not initialized'
    );
  }

  onDeletedComponentUpdates(): Observable<string> {
    return this.createSocketEventObservable<string>(
      IoCodeEnum.DeletedComponent,
      '[LiveUpdates] Cannot set up DeletedComponent listener - socket not initialized'
    );
  }

  onGadgetUsersUpdates(): Observable<GadgetUsersUpdate> {
    return this.createSocketEventObservable<GadgetUsersUpdate>(
      IoCodeEnum.GadgetUsersUpdated,
      '[LiveUpdates] Cannot set up GadgetUsersUpdated listener - socket not initialized'
    );
  }

  onGadgetSettingsUpdates(): Observable<ioGadgetSettings> {
    return this.createSocketEventObservable<ioGadgetSettings>(
      IoCodeEnum.GadgetSettings,
      '[LiveUpdates] Cannot set up GadgetSettings listener - socket not initialized. Listeners should be set up after socket connection.'
    );
  }

  onLabSettingsUpdates(): Observable<ioLabSettings> {
    return this.createSocketEventObservable<ioLabSettings>(
      IoCodeEnum.LabSettings,
      '[LiveUpdates] Cannot set up LabSettings listener - socket not initialized. Listeners should be set up after socket connection.',
      (ioLabSettings) => {
        console.log('[LiveUpdates] Received LabSettings event:', ioLabSettings);
      }
    );
  }

  onLabUsersUpdates(): Observable<any> {
    return this.createSocketEventObservable<LabUserCombo>(
      IoCodeEnum.LabUsersUpdated,
      '[LiveUpdates] Cannot set up LabUsersUpdated listener - socket not initialized. Listeners should be set up after socket connection.'
    );
  }

  onLabUsersDeleted(): Observable<{ labId: string, userIds: string[] }> {
    return this.createSocketEventObservable<{ labId: string, userIds: string[] }>(
      IoCodeEnum.LabUsersDeleted,
      '[LiveUpdates] Cannot set up LabUsersDeleted listener - socket not initialized. Listeners should be set up after socket connection.'
    );
  }

  onLabUserRequestDeleted(): Observable<{ labUserRequestId: string, labId: string }> {
    console.log('[LiveUpdates] Setting up listener for LabUserRequestDeleted event');
    console.log('[LiveUpdates] Socket connection status:', {
      exists: !!this.socket,
      connected: this.socket?.connected,
      id: this.socket?.id,
      transport: this.socket?.io?.engine?.transport?.name
    });

    return this.createSocketEventObservable<{ labUserRequestId: string, labId: string }>(
      IoCodeEnum.LabUserRequestDeleted,
      '[LiveUpdates] Cannot set up LabUserRequestDeleted listener - socket not initialized',
      (data) => {
        console.log('[LiveUpdates] Received LabUserRequestDeleted event:', data);
      }
    );
  }

  onGadgetCompleteUpdates(): Observable<GadgetComplete> {
    return this.createSocketEventObservable<GadgetComplete>(
      IoCodeEnum.GadgetComplete,
      '[LiveUpdates] Cannot set up GadgetComplete listener - socket not initialized'
    );
  }

  onNewLabCompleteUpdates(): Observable<LabComplete> {
    return this.createSocketEventObservable<LabComplete>(
      IoCodeEnum.LabComplete,
      '[LiveUpdates] Cannot set up LabComplete listener - socket not initialized'
    );
  }

  onCreatedLabUserUpdates(): Observable<LabUserComplete> {
    return this.createSocketEventObservable<LabUserComplete>(
      IoCodeEnum.LabUserCreated,
      '[LiveUpdates] Cannot set up LabUserCreated listener - socket not initialized'
    );
  }

  onNewGadgetUsersUpdates(): Observable<GadgetUserComplete[]> {
    return this.createSocketEventObservable<GadgetUserComplete[]>(
      IoCodeEnum.CreatedGadgetUsersForAllLabUsers,
      '[LiveUpdates] Cannot set up CreatedGadgetUsersForAllLabUsers listener - socket not initialized'
    );
  }

  onComponentScheduleNextDateUpdates(): Observable<ComponentSchedule[]> {
    return this.createSocketEventObservable<ComponentSchedule[]>(
      IoCodeEnum.ComponentScheduleNextDate,
      '[LiveUpdates] Cannot set up ComponentScheduleNextDate listener - socket not initialized'
    );
  }

  onComponentSettingsUpdates(): Observable<ComponentSettingsReturn> {
    return this.createSocketEventObservable<ComponentSettingsReturn>(
      IoCodeEnum.ComponentSettings,
      '[LiveUpdates] Cannot set up ComponentSettings listener - socket not initialized'
    );
  }

  onDevicePresenceUpdates(): Observable<DevicePresence> {
    this.bindDevicePresenceUpdates();
    return this.devicePresenceUpdatedSubject.asObservable();
  }

  onContactUpdates(): Observable<UserContact> {
    return this.createSocketEventObservable<UserContact>(
      'contactUpdates',
      '[LiveUpdates] Cannot set up contactUpdates listener - socket not initialized'
    );
  }

  onLiveUserNotificationUpdates(): Observable<UserNotification> {
    return this.createSocketEventObservable<UserNotification>(
      'userNotification',
      '[LiveUpdates] Cannot set up userNotification listener - socket not initialized. Listeners should be set up after socket connection.',
      (data) => {
        console.log('[LiveUpdates] [DupUser] Received userNotification:', data);
      }
    );
  }

  onLivelabControllerCompleteUpdates(): Observable<LabControllerComplete> {
    return this.createSocketEventObservable<LabControllerComplete>(
      IoCodeEnum.UpdatedLabController,
      '[LiveUpdates] Cannot set up UpdatedLabController listener - socket not initialized'
    );
  }

  triggerLocalContactUpdates(userContact: UserContact) {
    this.afterUserContactUpdatedObservable.next(userContact);
  }

  // Debug method to test connection
  async testConnection(): Promise<void> {
    console.log('[LiveUpdates] Testing connection to:', connLink);

    try {
      // Test HTTP endpoint
      const httpResponse = await fetch(`${connLink}/testConnection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      console.log('[LiveUpdates] HTTP test result:', httpResponse.ok);

      // Test WebSocket connection
      const wsTest = await this.checkWebSocketAvailability();
      console.log('[LiveUpdates] WebSocket test result:', wsTest);

    } catch (error) {
      console.error('[LiveUpdates] Connection test failed:', error);
    }
  }

  async testIO() {
    console.log('[LiveUpdates] testIO() requested. State:', {
      socketExists: !!this.socket,
      connected: this.socket?.connected,
      isConnecting: this.isConnecting,
      url: connLink
    });

    const connected = await this.ensureSocketConnected('testIO');

    if (connected && this.socket?.connected) {
      console.log('[LiveUpdates] Sending testIOTrigger');
      this.socket.emit('testIOTrigger', { timestamp: new Date().toISOString() });
    } else {
      const status = !this.socket ? 'Socket not initialized' : 'Socket disconnected';
      console.error(`[LiveUpdates] Cannot send testIOTrigger - ${status}`);
      this.matSnackBar.open(`Socket not connected: ${status}`, 'Close', { duration: 3000 });
    }
  }
}